/* LaosMap — projects Laos ADM1 province boundaries to SVG, renders a sales
   choropleth and zooms into a province on click. Dependency-free Mercator
   projection. Adapted from the design to consume real province data (props)
   and load the bundled /laos_adm1.geojson. */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { makeScale, matchProvinceId, fmtKip, fmtMillion } from './salesGeoHelpers';

const DEG = Math.PI / 180;
function mercY(lat) {
    return Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 180 / 2));
}

function eachCoord(geom, fn) {
    const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.type === 'MultiPolygon' ? geom.coordinates : [];
    polys.forEach((poly) => poly.forEach((ring) => ring.forEach(fn)));
}

function makeProjection(features, W, H, pad) {
    let minX = Infinity;
    let maxX = -Infinity;
    let minMy = Infinity;
    let maxMy = -Infinity;
    features.forEach((f) => {
        eachCoord(f.geometry, (c) => {
            const x = c[0] * DEG;
            const my = mercY(c[1]);
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (my < minMy) minMy = my;
            if (my > maxMy) maxMy = my;
        });
    });
    const spanX = maxX - minX;
    const spanY = maxMy - minMy;
    const scale = Math.min((W - 2 * pad) / spanX, (H - 2 * pad) / spanY);
    const offX = pad + (W - 2 * pad - spanX * scale) / 2;
    const offY = pad + (H - 2 * pad - spanY * scale) / 2;
    return function project(c) {
        return [offX + (c[0] * DEG - minX) * scale, offY + (maxMy - mercY(c[1])) * scale];
    };
}

function pathFor(geom, project) {
    const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.type === 'MultiPolygon' ? geom.coordinates : [];
    let d = '';
    polys.forEach((poly) => {
        poly.forEach((ring) => {
            ring.forEach((c, i) => {
                const p = project(c);
                d += (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1);
            });
            d += 'Z';
        });
    });
    return d;
}

function screenBBox(geom, project) {
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    eachCoord(geom, (c) => {
        const p = project(c);
        if (p[0] < x0) x0 = p[0];
        if (p[0] > x1) x1 = p[0];
        if (p[1] < y0) y0 = p[1];
        if (p[1] > y1) y1 = p[1];
    });
    return { x0, y0, x1, y1, cx: (x0 + x1) / 2, cy: (y0 + y1) / 2, w: x1 - x0, h: y1 - y0 };
}

function labelPoint(geom, project) {
    const polys = geom.type === 'Polygon' ? [geom.coordinates] : geom.type === 'MultiPolygon' ? geom.coordinates : [];
    let best = null;
    let bestArea = -1;
    polys.forEach((poly) => {
        const ring = poly[0];
        let area = 0;
        let cx = 0;
        let cy = 0;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
            const a = ring[j];
            const b = ring[i];
            const f = a[0] * b[1] - b[0] * a[1];
            area += f;
            cx += (a[0] + b[0]) * f;
            cy += (a[1] + b[1]) * f;
        }
        area *= 0.5;
        if (Math.abs(area) > bestArea) {
            bestArea = Math.abs(area);
            best = [cx / (6 * area), cy / (6 * area)];
        }
    });
    return best ? project(best) : null;
}

const GEO_LOCAL = '/laos_adm1.geojson';
const GEO_CDN =
    'https://media.githubusercontent.com/media/wmgeolab/geoBoundaries/main/releaseData/gbOpen/LAO/ADM1/geoBoundaries-LAO-ADM1_simplified.geojson';
const CACHE_KEY = 'lao_adm1_geojson_v1';

function useLaosGeo() {
    const [state, setState] = useState({ status: 'loading', features: null });
    useEffect(() => {
        let alive = true;
        function ingest(text) {
            const gj = JSON.parse(text);
            if (alive) setState({ status: 'ready', features: gj.features });
        }
        fetch(GEO_LOCAL)
            .then((r) => {
                if (!r.ok) throw new Error('no local');
                return r.text();
            })
            .then(ingest)
            .catch(() => {
                const cached = localStorage.getItem(CACHE_KEY);
                if (cached) {
                    try {
                        ingest(cached);
                        return;
                    } catch (e) {
                        /* fall through to CDN */
                    }
                }
                fetch(GEO_CDN)
                    .then((r) => r.text())
                    .then((t) => {
                        try {
                            localStorage.setItem(CACHE_KEY, t);
                        } catch (e) {
                            /* ignore quota */
                        }
                        ingest(t);
                    })
                    .catch(() => {
                        if (alive) setState({ status: 'error', features: null });
                    });
            });
        return () => {
            alive = false;
        };
    }, []);
    return state;
}

const LaosMap = (props) => {
    const { provinces, byId, selectedId, onSelect, accent, scaleStops, showLabels = true, showBorders = true } = props;
    const geo = useLaosGeo();
    const [hovered, setHovered] = useState(null);
    const W = 540;
    const H = 735;
    const PAD = 20;

    const model = useMemo(() => {
        if (geo.status !== 'ready') return null;
        const project = makeProjection(geo.features, W, H, PAD);
        const items = geo.features.map((f, i) => {
            const lp = labelPoint(f.geometry, project) || [0, 0];
            const prId = matchProvinceId(f.properties);
            const meta = prId ? byId[prId] : null;
            return {
                key: meta ? meta.pr_id : 'f' + i,
                meta,
                d: pathFor(f.geometry, project),
                bbox: screenBBox(f.geometry, project),
                label: lp
            };
        });
        const vals = items.map((it) => (it.meta ? it.meta.sales : 0));
        const min = Math.min.apply(null, vals);
        const max = Math.max.apply(null, vals);
        return { project, items, min, max };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [geo.status, byId]);

    const scale = useMemo(() => makeScale(scaleStops), [scaleStops]);

    const fullVB = { x: 0, y: 0, w: W, h: H };
    const target = useMemo(() => {
        if (!model || !selectedId) return fullVB;
        const it = model.items.find((x) => x.key === selectedId);
        if (!it) return fullVB;
        const b = it.bbox;
        const ar = W / H;
        const pad = 0.16;
        let w = b.w * (1 + pad * 2);
        let h = b.h * (1 + pad * 2);
        if (w / h > ar) h = w / ar;
        else w = h * ar;
        return { x: b.cx - w / 2, y: b.cy - h / 2, w, h };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model, selectedId]);

    const vbRef = useRef(fullVB);
    const rafRef = useRef(0);
    const [, setTick] = useState(0);
    useEffect(() => {
        const from = vbRef.current;
        const to = target;
        const t0 = performance.now();
        const dur = 720;
        function ease(t) {
            return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        }
        cancelAnimationFrame(rafRef.current);
        function step(now) {
            const p = Math.min(1, (now - t0) / dur);
            const e = ease(p);
            vbRef.current = {
                x: from.x + (to.x - from.x) * e,
                y: from.y + (to.y - from.y) * e,
                w: from.w + (to.w - from.w) * e,
                h: from.h + (to.h - from.h) * e
            };
            setTick((n) => n + 1);
            if (p < 1) rafRef.current = requestAnimationFrame(step);
        }
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [target]);

    if (geo.status === 'loading') {
        return React.createElement(
            'div',
            { className: 'map-state' },
            React.createElement('div', { className: 'spinner' }),
            React.createElement('p', null, 'ກຳລັງໂຫຼດແຜນທີ່ · Loading map…')
        );
    }
    if (geo.status === 'error' || !model) {
        return React.createElement(
            'div',
            { className: 'map-state' },
            React.createElement('p', null, 'ບໍ່ສາມາດໂຫຼດແຜນທີ່ໄດ້ · Could not load boundary data.')
        );
    }

    const vb = vbRef.current;
    const fontScale = vb.w / W;
    return React.createElement(
        'div',
        { className: 'map-wrap' },
        React.createElement(
            'svg',
            {
                viewBox: vb.x.toFixed(1) + ' ' + vb.y.toFixed(1) + ' ' + vb.w.toFixed(1) + ' ' + vb.h.toFixed(1),
                className: 'lao-svg',
                preserveAspectRatio: 'xMidYMid meet'
            },
            React.createElement(
                'g',
                null,
                model.items.map((it) => {
                    const sales = it.meta ? it.meta.sales : 0;
                    const t = model.max > model.min ? (sales - model.min) / (model.max - model.min) : 0.5;
                    const isSel = selectedId === it.key;
                    const isHov = hovered === it.key;
                    const dim = selectedId && !isSel;
                    return React.createElement('path', {
                        key: it.key,
                        d: it.d,
                        className: 'prov' + (isSel ? ' sel' : '') + (dim ? ' dim' : ''),
                        fill: scale(0.12 + t * 0.88),
                        stroke: showBorders ? (isSel ? accent : '#ffffff') : 'transparent',
                        strokeWidth: isSel ? 1.6 : 0.8,
                        vectorEffect: 'non-scaling-stroke',
                        onMouseEnter: () => setHovered(it.key),
                        onMouseLeave: () => setHovered(null),
                        onClick: () => {
                            if (it.meta) onSelect(isSel ? null : it.key);
                        },
                        style: { filter: isHov && !isSel ? 'brightness(1.05)' : 'none' }
                    });
                }),
                showLabels &&
                    model.items.map((it) => {
                        if (!it.meta || !it.label) return null;
                        const sel = selectedId === it.key;
                        const zoomed = !!selectedId; // a province is selected → map is zoomed in
                        // fontScale keeps apparent size ~constant across zoom; the base
                        // multiplier sets how big the label reads on screen. When zoomed we
                        // boost everything (selected biggest, neighbours mid-size) and keep
                        // every province's label visible so neighbouring values still show.
                        // The stroke halo is scaled by fontScale so it never blows up.
                        const nameSize = (sel ? 24 : zoomed ? 15 : 10.5) * fontScale;
                        const valSize = (sel ? 18 : zoomed ? 11.5 : 8.5) * fontScale;
                        const halo = (sel ? 3 : zoomed ? 2.4 : 2.2) * fontScale;
                        return React.createElement(
                            'text',
                            {
                                key: 't' + it.key,
                                x: it.label[0],
                                y: it.label[1],
                                className: 'prov-label' + (sel ? ' active' : ''),
                                textAnchor: 'middle',
                                style: {
                                    fontSize: nameSize.toFixed(2) + 'px',
                                    strokeWidth: halo.toFixed(2) + 'px',
                                    opacity: sel || !zoomed ? 1 : 0.85
                                }
                            },
                            React.createElement('tspan', { x: it.label[0], dy: 0 }, it.meta.lao),
                            React.createElement(
                                'tspan',
                                {
                                    x: it.label[0],
                                    dy: '1.5em',
                                    className: 'prov-label-val',
                                    style: { fill: accent, fontSize: valSize.toFixed(2) + 'px', strokeWidth: halo.toFixed(2) + 'px' }
                                },
                                zoomed ? fmtMillion(it.meta.sales) : fmtKip(it.meta.sales)
                            )
                        );
                    })
            )
        ),
        hovered &&
            (() => {
                const it = model.items.find((x) => x.key === hovered);
                if (!it || !it.meta) return null;
                return React.createElement(
                    'div',
                    { className: 'map-hint' },
                    React.createElement('span', { className: 'mh-lao' }, it.meta.lao),
                    React.createElement('span', { className: 'mh-en' }, it.meta.en),
                    React.createElement('span', { className: 'mh-val' }, fmtKip(it.meta.sales))
                );
            })()
    );
};

export default LaosMap;
